import React, { useEffect, useState } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { locationsExtra as locations } from "@/data/locations-extra";
import { useLang } from "@/lib/i18n";
import { useSEO, breadcrumbJsonLd, localBusinessJsonLd, faqPageJsonLd, siteUrl } from "@/lib/seo";
import { fetchFAQs } from "@/lib/api";
import { MapPin, Phone, Mail, ArrowRight, ArrowLeft, Truck } from "lucide-react";
import ContactForm from "@/components/ContactForm";
import FAQAccordion from "@/components/FAQAccordion";
import LocationRelatedSection from "@/components/LocationRelatedSection";

const LocationPageExtra = () => {
  const routerLocation = useLocation();
  const slug = routerLocation.pathname.replace(/^\/truckwash-/, "");
  const location = locations[slug];
  const { lang, t } = useLang();
  const [globalFaqs, setGlobalFaqs] = useState([]);

  useEffect(() => {
    fetchFAQs("truckwash").then(setGlobalFaqs).catch(() => {});
  }, []);

  const content = (location && location[lang]) || (location && location.nl) || {
    title: "", meta_title: "", meta_desc: "", h1: "", lead: "", paragraphs: [], service_areas: [],
    sectors_title: "", sectors: [], kpi_title: "", kpis: [],
  };
  const canonical = location ? siteUrl(`/truckwash-${location.slug}`) : siteUrl("/");

  // City-specific FAQ list (already structured {q,a}). Adapt to FAQAccordion shape (q_nl/q_en/a_nl/a_en).
  const cityFaqs = location && location.faq
    ? (location.faq[lang] || location.faq.nl || []).map((f) => ({
        id: f.id,
        q_nl: location.faq.nl.find((x) => x.id === f.id)?.q || f.q,
        a_nl: location.faq.nl.find((x) => x.id === f.id)?.a || f.a,
        q_en: location.faq.en.find((x) => x.id === f.id)?.q || f.q,
        a_en: location.faq.en.find((x) => x.id === f.id)?.a || f.a,
      }))
    : [];
  const combinedFaqsForJsonLd = [...cityFaqs, ...globalFaqs.slice(0, 3)];

  useSEO({
    title: content.meta_title,
    description: content.meta_desc,
    canonical,
    image: location ? location.image : undefined,
    lang,
    jsonLd: location
      ? [
          {
            id: "localbiz-jsonld",
            data: localBusinessJsonLd({ city: location.city, region: location.region }),
          },
          {
            id: "breadcrumb-jsonld",
            data: breadcrumbJsonLd([
              { name: "Home", url: siteUrl("/") },
              { name: content.title, url: canonical },
            ]),
          },
          ...(combinedFaqsForJsonLd.length > 0
            ? [{ id: "faq-jsonld", data: faqPageJsonLd(combinedFaqsForJsonLd, lang) }]
            : []),
        ]
      : [],
  });

  if (!location) return <Navigate to="/" replace />;

  return (
    <main data-testid={`location-page-${location.slug}`} className="min-h-screen">
      <section className="relative pt-14 pb-16 sm:pt-20 sm:pb-24">
        <div className="absolute inset-0 -z-10">
          <img src={location.image} alt={`Truckwash ${location.city}`} className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050509]/70 via-[#050509]/85 to-[#050509]" />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Link to="/" className="text-xs text-white/50 hover:text-[#00D4FF] inline-flex items-center gap-1.5 mb-6">
            <ArrowLeft className="w-3 h-3" /> {t("common.back")}
          </Link>
          <p className="section-overline mb-3 inline-flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> {location.region} · {lang === "nl" ? "Lokale service" : "Local service"}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white max-w-4xl leading-[1.05]">
            {content.h1}
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed">{content.lead}</p>
        </div>
      </section>

      <section className="py-12 sm:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">
              {lang === "nl" ? `Truckwash op locatie in ${location.city}` : `On-site truckwash in ${location.city}`}
            </h2>
            {content.paragraphs.map((p, i) => (
              <p key={i} className="text-white/65 text-base sm:text-[17px] leading-relaxed">
                {p}
              </p>
            ))}

            {/* KPIs */}
            {content.kpis && content.kpis.length > 0 && (
              <div className="pt-6">
                <p className="section-overline mb-4">{content.kpi_title}</p>
                <div className="grid grid-cols-3 gap-3">
                  {content.kpis.map((k, i) => (
                    <div key={i} className="rounded-xl border border-white/10 bg-[#0F0F23] p-5">
                      <div className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">{k.v}</div>
                      <div className="text-[11px] uppercase tracking-[0.16em] text-white/45 mt-1.5 font-medium">{k.l}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sectors */}
            {content.sectors && content.sectors.length > 0 && (
              <div className="pt-6">
                <p className="section-overline mb-4">{content.sectors_title}</p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {content.sectors.map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-white/70 text-[15px]">
                      <Truck className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pt-4">
              <p className="section-overline mb-3">{lang === "nl" ? "Werkgebied" : "Service areas"}</p>
              <div className="flex flex-wrap gap-2">
                {content.service_areas.map((a) => (
                  <span
                    key={a}
                    className="px-3 py-1.5 rounded-md border border-white/10 bg-[#0F0F23] text-xs text-white/70"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="sticky top-24 space-y-4">
              <div className="rounded-2xl border border-white/10 bg-[#0F0F23] p-7">
                <h3 className="text-xl font-bold text-white mb-4">
                  {lang === "nl" ? `Bel of mail direct voor ${location.city}` : `Call or email for ${location.city}`}
                </h3>
                <ul className="space-y-3 text-sm text-white/70">
                  <li className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-[#00D4FF]" />
                    <a href="tel:0365255021" className="hover:text-white">036 525 5021</a>
                  </li>
                  <li className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-[#00D4FF]" />
                    <a href="mailto:info@wasz.nl" className="hover:text-white">info@wasz.nl</a>
                  </li>
                  <li className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-[#00D4FF] mt-0.5 shrink-0" />
                    <div>
                      <div>{lang === "nl" ? "Postadres" : "Postal address"}: Scharstraat 25, 1317 PL Almere</div>
                      <div className="text-white/45 text-xs mt-1 italic">
                        {lang === "nl"
                          ? "Wij werken volledig mobiel — op uw locatie."
                          : "We operate fully mobile — at your location."}
                      </div>
                    </div>
                  </li>
                </ul>
                <Link to="/contact?type=quote">
                  <button className="btn-primary w-full justify-center mt-6">
                    {t("common.request_quote")} <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>

              {/* Map embed */}
              <div className="rounded-2xl border border-white/10 bg-[#0F0F23] overflow-hidden">
                <div className="aspect-[4/3] relative">
                  <iframe
                    data-testid={`location-map-${location.slug}`}
                    title={lang === "nl" ? `Servicegebied ${location.city}` : `Service area ${location.city}`}
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(location.city + ", Nederland")}&t=&z=11&ie=UTF8&iwloc=&output=embed`}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="absolute inset-0 w-full h-full grayscale-[0.4] contrast-110 invert hue-rotate-180"
                    style={{ border: 0 }}
                  />
                </div>
                <div className="p-4 border-t border-white/10 text-xs text-white/45">
                  <MapPin className="w-3 h-3 inline text-[#00D4FF] mr-1" />
                  {lang === "nl" ? "Servicegebied" : "Service area"} {location.city}, {location.region}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {cityFaqs.length > 0 && (
        <section className="py-16 sm:py-24 bg-[#0a0a18] border-y border-white/5">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">
            <div className="text-center mb-10">
              <p className="section-overline mb-3">FAQ</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                {lang === "nl" ? `Veelgestelde vragen — ${location.city}` : `FAQ — ${location.city}`}
              </h2>
            </div>
            <FAQAccordion items={cityFaqs} dataTestid={`location-faq-${location.slug}`} />
          </div>
        </section>
      )}

      {/* Gerelateerde diensten en regio's — SEO interne linkstructuur */}
      <LocationRelatedSection slug={location.slug} city={location.city} lang={lang} />

      <section className="py-20">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <ContactForm
            defaultType="quote"
            dataTestid={`location-${location.slug}-form`}
            title={lang === "nl" ? `Offerte voor ${location.city}` : `Quote for ${location.city}`}
            subtitle={lang === "nl" ? "Vermeld vlootomvang en gewenste cyclus." : "Mention fleet size and preferred cycle."}
            hideTypeSelector
          />
        </div>
      </section>
    </main>
  );
};

export default LocationPageExtra;
